// Ô đối chiếu "Lịch hẹn (lúc tư vấn) → Đơn POS": tổng chênh lệch + dòng thêm / bỏ / đổi
import { useState } from 'react';
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import type { AppointmentDiff as Diff } from '../appointmentLink';
import { money } from '../utils';

interface Props {
  diff: Diff;
  /** Nhãn cột phải: "Đơn POS" khi đang lên đơn, "Thực thu" khi đơn đã tạo */
  posLabel?: string;
  defaultOpen?: boolean;
  className?: string;
}

export default function AppointmentDiff({ diff, posLabel = 'Đơn POS', defaultOpen = false, className = '' }: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const details = diff.added.length + diff.removed.length + diff.changed.length + (diff.extraDiscount > 0 ? 1 : 0);
  const tone = diff.diff < 0 ? 'text-green-700' : diff.diff > 0 ? 'text-amber-700' : 'text-slate-500';

  return (
    <div className={`rounded-lg border border-slate-200 bg-white text-sm ${className}`}>
      <button
        type="button"
        onClick={() => details && setOpen(o => !o)}
        className={`flex w-full items-center gap-2 px-3 py-2 text-left ${details ? 'cursor-pointer' : 'cursor-default'}`}
        aria-expanded={open}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-slate-500">Lịch hẹn</span>
            <span className="font-semibold text-slate-700">{money(diff.appointmentTotal)}</span>
            <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
            <span className="text-slate-500">{posLabel}</span>
            <span className="font-semibold text-slate-800">{money(diff.posTotal)}</span>
          </div>
        </div>
        <span className={`shrink-0 font-bold ${tone}`}>
          {diff.diff === 0 ? 'Khớp' : `${diff.diff > 0 ? '+' : '−'}${money(Math.abs(diff.diff))}`}
        </span>
        {details > 0 && (open ? <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" /> : <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />)}
      </button>
      {open && details > 0 && (
        <ul className="space-y-1 border-t border-slate-100 px-3 py-2 text-xs">
          {diff.extraDiscount > 0 && (
            <li className="flex justify-between gap-2 text-green-700"><span>Giảm thêm tại quầy (KM, CK, voucher)</span><span>−{money(diff.extraDiscount)}</span></li>
          )}
          {diff.added.map((a, i) => (
            <li key={`a${i}`} className="flex justify-between gap-2 text-slate-700"><span>+ Thêm {a.name}{a.qty > 1 ? ` ×${a.qty}` : ''}</span><span>+{money(a.amount)}</span></li>
          ))}
          {diff.removed.map((r, i) => (
            <li key={`r${i}`} className="flex justify-between gap-2 text-red-600"><span>− Bỏ {r.name}{r.qty > 1 ? ` ×${r.qty}` : ''}</span><span>−{money(r.amount)}</span></li>
          ))}
          {diff.changed.map((c, i) => (
            <li key={`c${i}`} className="flex justify-between gap-2 text-slate-600"><span>{c.name}</span><span>{c.from} → {c.to}</span></li>
          ))}
        </ul>
      )}
    </div>
  );
}
